import { useState } from "react";
import { useNavigate, Navigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Scale, ArrowRight, Loader2 } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/lib/i18n";
import LanguageToggle from "@/components/LanguageToggle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const FADE_IN = { initial: { opacity: 0, y: 10 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.4 } };

export default function Login() {
  const { finishAuth, user } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [accept, setAccept] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (mode === "register" && !accept) { setErr(t("login.consentRequired")); return; }
    setBusy(true);
    try {
      const path = mode === "login" ? "/auth/login" : "/auth/register";
      const body = mode === "login" ? { email, password } : { email, password, name, accept_terms: accept };
      const { data } = await api.post(path, body);
      finishAuth(data.user);
      nav("/dashboard");
    } catch (e2) {
      setErr(apiErr(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left panel — brand / editorial */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 overflow-hidden border-r border-zinc-800/80">
        <img
          src="https://images.unsplash.com/photo-1498262257252-c282316270bc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGFyY2hpdGVjdHVyZSUyMGRhcmt8ZW58MHx8fHwxNzg3NDUyMjc2fDA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="absolute inset-0 h-full w-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-[#0a0a0c]/60" />
        <div className="relative flex items-center gap-2.5 text-zinc-100">
          <Scale className="h-5 w-5 text-[#d4af37]" strokeWidth={1.5} />
          <span className="font-mono-ui text-sm tracking-[0.2em] uppercase">Lexiqa</span>
        </div>
        <div className="relative">
          <h1 className="font-serif-display text-5xl font-light leading-[1.05] text-zinc-50">
            {t("landing.heroA")}
            <br />
            <span className="italic text-zinc-300">{t("landing.heroB")}</span>{t("landing.heroC")}
          </h1>
          <p className="mt-6 max-w-md text-zinc-400 leading-relaxed">
            {t("login.leftP")}
          </p>
        </div>
        <div className="relative font-mono-ui text-[11px] text-zinc-600 tracking-wider">
          {t("login.leftFoot")}
        </div>
      </div>

      {/* Right panel — form */}
      <div className="relative flex items-center justify-center p-6">
        <div className="absolute top-5 right-6">
          <LanguageToggle />
        </div>
        <motion.div
          {...FADE_IN}
          className="w-full max-w-sm"
        >
          <div className="lg:hidden mb-8 flex items-center gap-2.5">
            <Scale className="h-5 w-5 text-[#d4af37]" strokeWidth={1.5} />
            <span className="font-mono-ui text-sm tracking-[0.2em] uppercase">Lexiqa</span>
          </div>
          <p className="font-mono-ui text-[11px] uppercase tracking-[0.15em] text-zinc-500">
            {mode === "login" ? t("login.modeLogin") : t("login.modeRegister")}
          </p>
          <h2 className="mt-2 font-serif-display text-3xl font-light text-zinc-50">
            {mode === "login" ? t("login.headLogin") : t("login.headRegister")}
          </h2>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="auth-form">
            {mode === "register" && (
              <div>
                <Label className="text-zinc-400 text-xs">{t("login.name")}</Label>
                <Input
                  data-testid="name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t("login.namePh")}
                  className="mt-1.5 bg-[#0a0a0c] border-zinc-800 focus-visible:ring-[#d4af37]/40"
                />
              </div>
            )}
            <div>
              <Label className="text-zinc-400 text-xs">Email</Label>
              <Input
                data-testid="email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("login.emailPh")}
                className="mt-1.5 bg-[#0a0a0c] border-zinc-800 focus-visible:ring-[#d4af37]/40"
                required
              />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs">{t("login.password")}</Label>
              <Input
                data-testid="password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-1.5 bg-[#0a0a0c] border-zinc-800 focus-visible:ring-[#d4af37]/40"
                required
              />
            </div>

            {mode === "register" && (
              <label data-testid="consent-checkbox-label" className="flex items-start gap-2.5 cursor-pointer select-none">
                <input
                  data-testid="consent-checkbox"
                  type="checkbox"
                  checked={accept}
                  onChange={(e) => setAccept(e.target.checked)}
                  className="mt-0.5 h-4 w-4 shrink-0 rounded border-zinc-700 bg-[#0a0a0c] accent-[#d4af37] cursor-pointer"
                />
                <span className="text-xs leading-relaxed text-zinc-400">
                  {(() => {
                    const tpl = t("login.consent");
                    const [before, rest = ""] = tpl.split("{tos}");
                    const [middle, after = ""] = rest.split("{privacy}");
                    return (
                      <>
                        {before}
                        <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-[#d4af37] hover:underline">{t("login.consentTos")}</a>
                        {middle}
                        <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-[#d4af37] hover:underline">{t("login.consentPrivacy")}</a>
                        {after}
                      </>
                    );
                  })()}
                </span>
              </label>
            )}

            {err && (
              <p data-testid="auth-error" className="text-rose-400 text-sm font-mono-ui">
                {err}
              </p>
            )}

            <Button
              data-testid="submit-auth-btn"
              type="submit"
              disabled={busy || (mode === "register" && !accept)}
              className="w-full h-11 bg-zinc-50 text-zinc-950 hover:bg-white rounded-md font-medium group"
            >
              {busy ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  {mode === "login" ? t("login.signIn") : t("login.register")}
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </>
              )}
            </Button>
          </form>

          <div className="mt-5 flex items-center gap-3">
            <span className="h-px flex-1 bg-zinc-800" />
            <span className="font-mono-ui text-[10px] uppercase tracking-wider text-zinc-600">{t("common.or")}</span>
            <span className="h-px flex-1 bg-zinc-800" />
          </div>

          <button
            data-testid="google-login-btn"
            onClick={() => {
              // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
              const redirectUrl = window.location.origin + "/dashboard";
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            className="mt-4 w-full h-11 flex items-center justify-center gap-2.5 rounded-md border border-zinc-700 bg-[#121216] text-zinc-100 hover:bg-zinc-800 transition-colors"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24"><path fill="#EA4335" d="M12 10.2v3.9h5.5c-.24 1.4-1.7 4.1-5.5 4.1A6.2 6.2 0 1 1 12 5.8c1.9 0 3.1.8 3.8 1.5l2.6-2.5A9.6 9.6 0 1 0 12 21.6c5.5 0 9.2-3.9 9.2-9.4 0-.6-.07-1.1-.16-1.6H12z"/></svg>
            {t("login.google")}
          </button>

          <button
            data-testid="toggle-mode-btn"
            onClick={() => {
              setMode(mode === "login" ? "register" : "login");
              setErr("");
            }}
            className="mt-6 text-sm text-zinc-500 hover:text-zinc-300 transition-colors"
          >
            {mode === "login" ? t("login.toRegister") : t("login.toLogin")}
          </button>
        </motion.div>
      </div>
    </div>
  );
}
